import type { components } from '@connected/api-client/api.generated';
import type { FormApi, ReactFormApi } from '@tanstack/react-form';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { validators } from '@connected/api-client/generated/validators.generated';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import {
    IB22SingleOptionItem,
    SingleHorizontalOptionItemWithoutDot,
    SingleOptionField,
} from '@connected/form-fields/components/option';
import { useTranslation } from 'react-i18next';

import type { Remark } from '../utils/observation-types';

type ObservationReportFieldsProps = {
    form: FormApi<Remark, ZodValidator> & ReactFormApi<Remark, ZodValidator>;
    reportIndex: number;
    inspectionKind: components['schemas']['ElectricalInspectionKind'];
    classificationData: components['schemas']['ListElectricalObservationClassificationForProjectQueryResult'] | undefined;
    disabled: boolean;
    isSingleClassification?: boolean;
};

export function ObservationReportFields({
    form,
    reportIndex,
    inspectionKind,
    classificationData,
    disabled,
    isSingleClassification = false,
}: ObservationReportFieldsProps) {
    const isScope = inspectionKind.startsWith('Scope');
    const { t } = useTranslation('re-inspections');

    // Determine field names based on whether it's single classification or report-based
    function getFieldName<T extends keyof Remark>(field: T) {
        return `reports[${reportIndex}].${field}` as const; // Report-based field names
    }

    return (
        <>
            {/* Only show fields if single classification OR enabled is true */}
            <form.Subscribe selector={state => isSingleClassification ? true : state.values.reports[reportIndex]?.enabled}>
                {(enabled) => {
                    // For single classification, always show fields
                    // For combination inspection, only show if enabled is true
                    const shouldShowFields = isSingleClassification || enabled === true;

                    if (!shouldShowFields) {
                        return null;
                    }

                    return (
                        <>
                            {/* Classification Group (Scope only) */}
                            {isScope
                                ? (
                                        <>
                                            <form.Field
                                                name={getFieldName('electricalObservationClassificationGroupId')}
                                                validators={{
                                                    onChange: validators.createElectricalProjectObservationCommand.createElectricalProjectObservationCommandReport.electricalObservationClassificationGroupId,
                                                }}
                                            >
                                                {({ handleChange, state, name }) => (
                                                    <Label label={t('observations.form.classificationGroup')} className="text-sm font-medium">
                                                        <SingleOptionField
                                                            name={name}
                                                            handleChange={(e) => {
                                                                handleChange(e);
                                                                form.setFieldValue(getFieldName('electricalObservationClassificationCodeId'), null);
                                                            }}
                                                            className="flex flex-row items-center justify-between"
                                                            state={state}
                                                            options={classificationData?.Groups?.map(group => ({
                                                                label: group.Name!,
                                                                value: group.ElectricalObservationClassificationGroupId!,
                                                            })) || []}
                                                            render={SingleHorizontalOptionItemWithoutDot}
                                                            disabled={disabled}
                                                        />
                                                        <FieldInfo state={state} />
                                                    </Label>
                                                )}
                                            </form.Field>
                                            <form.Subscribe
                                                selector={(state) => {
                                                    return [state.values.reports[reportIndex]?.electricalObservationClassificationGroupId];
                                                }}
                                            >
                                                {([classificationGroupId]) => {
                                                    const selectedGroup = classificationData?.Groups?.find(
                                                        group => group.ElectricalObservationClassificationGroupId === classificationGroupId,
                                                    );

                                                    if (!selectedGroup)
                                                        return null;

                                                    return (
                                                        <div className="flex w-full flex-col gap-6 rounded-md bg-background border border-border p-6">
                                                            <div className="flex flex-col gap-2">
                                                                <p className="font-semibold text-sm">{selectedGroup.Description}</p>
                                                                <div className="flex flex-col gap-2">
                                                                    {selectedGroup.Standards?.split('\r\n').map(
                                                                        line => <p key={line} className="text-text-tertiary text-sm font-medium">{line}</p>,
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-col gap-2">
                                                                {selectedGroup.Codes?.map(code => (
                                                                    <div key={code.ElectricalObservationClassificationCodeId} className="flex items-center gap-4">
                                                                        <div
                                                                            className="flex min-h-4 min-w-4 items-center justify-center rounded-full text-white"
                                                                            style={{
                                                                                background: code.HexColorCode!,
                                                                            }}
                                                                        />
                                                                        <p className="text-text-tertiary text-sm font-medium">{code.Description}</p>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <form.Field
                                                                name={getFieldName('electricalObservationClassificationCodeId')}
                                                                validators={{
                                                                    onChange: validators.createElectricalProjectObservationCommand.createElectricalProjectObservationCommandReport.electricalObservationClassificationCodeId,
                                                                }}
                                                            >
                                                                {({ handleChange, state, name }) => (
                                                                    <>
                                                                        <SingleOptionField
                                                                            name={name}
                                                                            className="flex flex-row items-center justify-between"
                                                                            handleChange={handleChange}
                                                                            state={state}
                                                                            options={selectedGroup.Codes?.map(code => ({
                                                                                label: t(`classifications.${code.Kind}`),
                                                                                value: code.ElectricalObservationClassificationCodeId!,
                                                                                color: code.HexColorCode!,
                                                                            })) || []}
                                                                            render={IB22SingleOptionItem}
                                                                            disabled={disabled}
                                                                        />
                                                                        <FieldInfo state={state} />
                                                                    </>
                                                                )}
                                                            </form.Field>
                                                        </div>
                                                    );
                                                }}
                                            </form.Subscribe>
                                        </>
                                    )
                                : (
                                        <form.Field
                                            name={getFieldName('codeKind')}
                                            validators={{
                                                onChange: validators.createElectricalProjectObservationCommand.createElectricalProjectObservationCommandReport.codeKind,
                                            }}
                                        >
                                            {({ handleChange, state, name }) => (
                                                <Label label={t('observations.form.classification')}>
                                                    <SingleOptionField
                                                        name={name}
                                                        handleChange={handleChange}
                                                        className="flex flex-row items-center justify-between"
                                                        state={state}
                                                        options={classificationData?.Codes?.map(code => ({
                                                            label: t(`classifications.${code.Kind}`),
                                                            value: code.Kind!,
                                                            color: code.HexColorCode!,
                                                        })) || []}
                                                        render={IB22SingleOptionItem}
                                                        disabled={disabled}
                                                    />
                                                    <FieldInfo state={state} />
                                                </Label>
                                            )}
                                        </form.Field>
                                    )}

                        </>
                    );
                }}
            </form.Subscribe>
        </>
    );
}
